import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreatePartnershipPayload } from './dtos/create.dto';
import type { GetAllPartnershipsParams } from './dtos/getAll.dto';
import type {
  PausePartnershipDto,
  PausePartnershipPayload,
} from './dtos/pause.dto';
import type {
  UpdatePartnershipDto,
  UpdatePartnershipPayload,
} from './dtos/update.dto';
import type { PartnershipRepository } from './repository';
import type {
  ActivatePartnershipOutput,
  ActivatePartnershipResponse,
} from './types/activate.type';
import type {
  CancelPausePartnershipOutput,
  CancelPausePartnershipResponse,
} from './types/cancelPause.type';
import type {
  CreatePartnershipOutput,
  CreatePartnershipResponse,
} from './types/create.type';
import type {
  GetAllPartnershipsOutput,
  GetAllPartnershipsResponse,
} from './types/getAll.type';
import type {
  GetPartnershipByIdOutput,
  GetPartnershipByIdResponse,
} from './types/getById.type';
import type {
  PausePartnershipOutput,
  PausePartnershipResponse,
} from './types/pause.type';
import type {
  UpdatePartnershipOutput,
  UpdatePartnershipResponse,
} from './types/update.type';

class PartnershipService implements PartnershipRepository {
  public async getAll(
    params: GetAllPartnershipsParams,
  ): Promise<GetAllPartnershipsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllPartnershipsResponse,
      GetAllPartnershipsParams
    >({ path: 'partnerships', params });

    return { count: data.count, list: data.list, pagination: data.pagination };
  }

  public async getById(id: string): Promise<GetPartnershipByIdOutput> {
    const { data } = await backendHttpClient.get<GetPartnershipByIdResponse>({
      path: `partnerships/${id}`,
    });

    return { partnership: data.partnership };
  }

  public async create(
    payload: CreatePartnershipPayload,
  ): Promise<CreatePartnershipOutput> {
    const { data } = await backendHttpClient.post<
      CreatePartnershipResponse,
      CreatePartnershipPayload
    >({ path: 'partnerships', body: payload });

    return { partnership: data.partnership };
  }

  public async update({
    id,
    ...dto
  }: UpdatePartnershipPayload): Promise<UpdatePartnershipOutput> {
    const { data } = await backendHttpClient.patch<
      UpdatePartnershipResponse,
      UpdatePartnershipDto
    >({ path: `partnerships/${id}`, body: dto });

    return { partnership: data.partnership };
  }

  public async activate(id: string): Promise<ActivatePartnershipOutput> {
    const { data } = await backendHttpClient.patch<ActivatePartnershipResponse>(
      {
        path: `partnerships/${id}/activate`,
      },
    );

    return { partnership: data.partnership };
  }

  public async pause(
    payload: PausePartnershipPayload,
  ): Promise<PausePartnershipOutput> {
    const { data } = await backendHttpClient.patch<
      PausePartnershipResponse,
      PausePartnershipDto
    >({
      path: `partnerships/${payload.id}/pause`,
      body: {
        endDate: payload.endDate,
        startDate: payload.startDate,
      },
    });

    return { partnership: data.partnership };
  }

  public async cancelPause(id: string): Promise<CancelPausePartnershipOutput> {
    const { data } =
      await backendHttpClient.patch<CancelPausePartnershipResponse>({
        path: `partnerships/${id}/cancel-pause`,
      });

    return { partnership: data.partnership };
  }

  public async archive(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `partnerships/${id}`,
    });
  }

  public async unarchive(id: string): Promise<void> {
    await backendHttpClient.patch<void>({
      path: `partnerships/${id}/unarchive`,
    });
  }
}

export const partnershipService = new PartnershipService();
